import React, { useEffect } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { formatCompactNumber, formatNumber } from '../../../utils'
import { ExpandIcon, ContractIcon } from './Icons'

const GenderSection = ({
  safeGenderData,
  maleChartDomain,
  femaleChartDomain,
  genderDomainMax,
  selectedLine,
  showPolygonLines,
  showIndividualPolygonLines,
  singlePolygonData,
  aggSelector,
  rightSidebarStore,
  isExpanded,
  onToggleExpand
}) => {
  const expandedInnerHeight = Math.min(300, Math.max(140, (safeGenderData.length || 0) * 44 + 28))
  const chartHeight = isExpanded ? expandedInnerHeight : 110
  const chartLayoutKey = isExpanded ? 'expanded' : 'collapsed'
  const selectedYear = rightSidebarStore && (rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear)
  const animationId = `mf-${selectedYear || 'latest'}-${chartLayoutKey}`
  const axisStep = genderDomainMax > 0 ? Math.round(genderDomainMax / 3) : 100000
  const maleTicks = [-genderDomainMax, -axisStep * 2, -axisStep, 0]
  const femaleTicks = [0, axisStep, axisStep * 2, genderDomainMax]

  useEffect(() => {
    // Recharts ResponsiveContainer sometimes keeps stale dimensions
    // when parent width changes without a window resize event.
    const t1 = setTimeout(() => window.dispatchEvent(new Event('resize')), 0)
    const t2 = setTimeout(() => window.dispatchEvent(new Event('resize')), 120)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [isExpanded])

  const handleGenderDrilldown = () => {
    // Mirror trends-chart behavior: from folder view, click chart to show individual shapes.
    if (singlePolygonData) return
    if (showPolygonLines && showIndividualPolygonLines) return
    if (aggSelector === 'dissolved') return
    if (rightSidebarStore) {
      rightSidebarStore.showShapeLines()
    }
  }

  const handleGenderSeriesClick = (series) => {
    if (singlePolygonData) return
    if (!series || !rightSidebarStore) return
    if (!(showPolygonLines && showIndividualPolygonLines)) {
      handleGenderDrilldown()
      return
    }
    if (!series.id) return
    if (selectedLine === series.id) {
      rightSidebarStore.clearSelectedLine()
      return
    }
    rightSidebarStore.setSelectedLine(series.id, rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear)
  }

  return (
    <div className={`people-data-sidebar__section people-data-sidebar__section--gender ${isExpanded ? 'people-data-sidebar__section--expanded-chart' : ''}`}>
      <div className='people-data-sidebar__section-header'>
        <h3 className='people-data-sidebar__section-title'>Male Vs Female</h3>
        <button
          type='button'
          className='people-data-sidebar__expand-btn'
          onClick={onToggleExpand}
          aria-label={isExpanded ? 'Contract male vs female chart' : 'Expand male vs female chart'}
        >
          {isExpanded ? <ContractIcon /> : <ExpandIcon />}
        </button>
      </div>
      <div
        className='people-data-sidebar__gender-chart-container'
        style={{
          height: chartHeight,
          marginTop: isExpanded ? 'auto' : 0,
          marginBottom: isExpanded ? 'auto' : 0
        }}
      >
        <ResponsiveContainer key={`male-${chartLayoutKey}`} width='100%'>
          <BarChart
            layout='vertical'
            data={safeGenderData}
            margin={{ top: 0, right: 0, left: 25, bottom: 10 }}
          >
            <XAxis
              type='number'
              domain={maleChartDomain}
              ticks={maleTicks}
              tickFormatter={(val) => formatNumber(Math.abs(val))}
              stroke='#475569'
              fontSize={11.5}
              tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide type='category' dataKey='name' />
            <Tooltip
              cursor={{ fill: 'none' }}
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              formatter={(val, name) => {
                return [formatCompactNumber(Math.abs(val)), name === 'male' ? 'Male' : 'Female']
              }}
            />
            <Bar
              dataKey='male'
              cursor='pointer'
              barSize={25}
              activeBar={undefined}
              isAnimationActive
              animationDuration={450}
              animationEasing='ease-out'
              animationId={animationId}
            >
              {safeGenderData.map((payload, index) => {
                const isSelected = selectedLine === payload.id
                return (
                  <Cell
                    cursor='pointer'
                    fill='#5EB1BC'
                    fillOpacity={selectedLine ? (isSelected ? 1 : 0.35) : 1}
                    stroke={isSelected ? '#0F172A' : 'none'}
                    strokeWidth={isSelected ? 2 : 0}
                    key={`cell-male-${index}`}
                    onClick={() => handleGenderSeriesClick(payload)}
                    onMouseEnter={() => {
                      // TODO: Add polygon highlight on map if needed
                      // onHover(payload)
                    }}
                    onMouseLeave={() => {
                      // TODO: Remove polygon highlight on map if needed
                      // onExit(payload)
                    }}
                  />
                )
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <ResponsiveContainer key={`female-${chartLayoutKey}`} width='100%'>
          <BarChart
            layout='vertical'
            data={safeGenderData}
            margin={{ top: 0, right: 25, left: 0, bottom: 10 }}
          >
            <XAxis
              type='number'
              domain={femaleChartDomain}
              ticks={femaleTicks}
              tickFormatter={(val) => val === 0 ? '' : formatNumber(val)}
              stroke='#475569'
              fontSize={11.5}
              tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide type='category' dataKey='name' />
            <Tooltip
              cursor={{ fill: 'none' }}
              contentStyle={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              formatter={(val, name) => {
                return [formatCompactNumber(val), name === 'female' ? 'Female' : 'Male']
              }}
            />
            <Bar
              dataKey='female'
              cursor='pointer'
              barSize={25}
              isAnimationActive
              animationDuration={450}
              animationEasing='ease-out'
              animationId={animationId}
            >
              {safeGenderData.map((payload, index) => {
                const isSelected = selectedLine === payload.id
                return (
                  <Cell
                    cursor='pointer'
                    fill='#EC4899'
                    fillOpacity={selectedLine ? (isSelected ? 1 : 0.35) : 1}
                    stroke={isSelected ? '#0F172A' : 'none'}
                    strokeWidth={isSelected ? 2 : 0}
                    key={`cell-female-${index}`}
                    onClick={() => handleGenderSeriesClick(payload)}
                    onMouseEnter={() => {
                      // TODO: Add polygon highlight on map if needed
                      // onHover(payload)
                    }}
                    onMouseLeave={() => {
                      // TODO: Remove polygon highlight on map if needed
                      // onExit(payload)
                    }}
                  />
                )
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default GenderSection
